// ✅ src/pages/VehicleV2/FileManagerRoadTaxdoc.jsx
// File Manager Road Tax Document (React)
// ✅ NOW uses docId (NOT vehicleId)
// - Fetch list by docId
// - View document (open in new tab)
// - Remove selected documents

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { FaArrowLeft, FaTrash, FaEye } from "react-icons/fa";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// backend mount:
// app.use("/api/fileManagerRoadTaxDoc", router)
const API_BASE = "https://nissi-india.com/api/fileManagerRoadTaxDoc";

const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

export default function FileManagerRoadTaxdoc() {
  const navigate = useNavigate();
  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const { state } = useLocation();
  const [searchParams] = useSearchParams();

  // ✅ docId can come from:
  // 1) navigate("/vehicle/FileManagerRoadTaxDoc", { state: { docId } })
  // 2) /vehicle/FileManagerRoadTaxDoc?docId=123
  // 3) sessionStorage.setItem("RoadTaxDocId","123") (optional fallback)
  const docId = useMemo(() => {
    const fromState = toInt(state?.docId, 0);
    if (fromState) return fromState;

    const fromQS = toInt(searchParams.get("docId"), 0);
    if (fromQS) return fromQS;

    const fromSession = toInt(sessionStorage.getItem("RoadTaxDocId"), 0);
    if (fromSession) return fromSession;

    return 0;
  }, [state, searchParams]);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState({}); // { [fld_roadtaxdocumentid]: true/false }

  const allSelected = useMemo(() => {
    if (!rows.length) return false;
    return rows.every((r) => selected[r.fld_roadtaxdocumentid]);
  }, [rows, selected]);

  const anySelected = useMemo(() => Object.values(selected).some(Boolean), [selected]);

  const fetchDocs = async () => {
    if (!docId) {
      setRows([]);
      setSelected({});
      toast.info("Document ID not found");
      return;
    }

    setLoading(true);
    try {
      // ✅ NOW: list by docId
      const { data } = await axios.get(`${API_BASE}/list`, {
        params: { docId },
      });

      const list = data?.rows || data?.data || data || [];
      if (!Array.isArray(list) || list.length === 0) {
        setRows([]);
        setSelected({});
        toast.info("No Records Found");
        return;
      }

      setRows(list);
      setSelected({});
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to load documents");
      setRows([]);
      setSelected({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);

  const toggleOne = (id) => {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleAll = () => {
    if (!rows.length) return;
    if (allSelected) return setSelected({});
    const next = {};
    for (const r of rows) next[r.fld_roadtaxdocumentid] = true;
    setSelected(next);
  };

  const openDoc = (filename) => {
    if (!filename) return;

    // ✅ ASPX:
    // "../VehicleV2/RoadTaxDocument/" + filename
    const url = `/VehicleV2/RoadTaxDocument/${encodeURIComponent(filename)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const removeSelected = async () => {
    const ids = rows
      .filter((r) => selected[r.fld_roadtaxdocumentid])
      .map((r) => Number(r.fld_roadtaxdocumentid))
      .filter((n) => Number.isFinite(n) && n > 0);

    if (!ids.length) {
      toast.info("Select at least one document to remove");
      return;
    }

    const ok = window.confirm("Are you sure you want to delete selected documents?");
    if (!ok) return;

    setLoading(true);
    try {
      const { data } = await axios.post(`${API_BASE}/remove`, { ids, docId });

      if (data?.success === false) {
        toast.error(data?.message || "Document cannot be removed.");
      } else {
        toast.success(data?.message || "Deleted Successfully");
      }

      await fetchDocs();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Document cannot be removed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <VehicleLayoutWrapper>
      <HeaderBar />

      <ToastContainer position="top-right" autoClose={2500} />

      <div className="p-3">
        {/* Title + actions */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
          <div className="d-flex align-items-center gap-2">
            <button
              className={`btn ${buttonClass} ${hoverClass} text-white`}
              onClick={() => navigate(-1)}
              type="button"
            >
              <FaArrowLeft className="me-2" />
              Back
            </button>

            <h5 className="mb-0" style={{ fontWeight: 700 }}>
              File Manager Road Tax Document
            </h5>
          </div>

          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-outline-secondary"
              onClick={fetchDocs}
              disabled={loading}
              type="button"
            >
              Refresh
            </button>

            <button
              className="btn btn-danger"
              onClick={removeSelected}
              disabled={loading || !anySelected}
              type="button"
              title={!anySelected ? "Select documents to remove" : "Remove selected"}
            >
              <FaTrash className="me-2" />
              Remove
            </button>
          </div>
        </div>

        {/* Table */}
        <div
          className="card"
          style={{
            borderRadius: 10,
            border: isDark ? "1px solid #333" : "1px solid #e5e5e5",
          }}
        >
          <div className="card-body p-2">
            <div style={{ overflowX: "auto" }}>
              <table className="table table-bordered table-hover mb-0" style={{ minWidth: 800 }}>
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 70, whiteSpace: "nowrap" }}>S.No</th>
                    <th style={{ whiteSpace: "nowrap" }}>Document</th>
                    <th style={{ width: 120, whiteSpace: "nowrap" }}>View</th>
                    <th style={{ width: 120, whiteSpace: "nowrap" }}>
                      <div className="d-flex align-items-center justify-content-between">
                        <span>Select</span>
                        <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={4} className="text-center py-4">
                        Loading...
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center py-4">
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr key={r.fld_roadtaxdocumentid ?? idx}>
                        <td style={{ whiteSpace: "nowrap" }}>{idx + 1}</td>

                        <td style={{ wordBreak: "break-word" }}>{r.fld_document || ""}</td>

                        <td style={{ whiteSpace: "nowrap" }}>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => openDoc(r.fld_document)}
                          >
                            <FaEye className="me-1" />
                            Show
                          </button>
                        </td>

                        <td className="text-center" style={{ whiteSpace: "nowrap" }}>
                          <input
                            type="checkbox"
                            checked={!!selected[r.fld_roadtaxdocumentid]}
                            onChange={() => toggleOne(r.fld_roadtaxdocumentid)}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-end mt-2">
              <small className="text-muted">{rows.length ? `Total: ${rows.length}` : ""}</small>
            </div>
          </div>
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}
